/**
 * Shared Training building blocks — emit the CA CDL trainer design's EXACT class
 * names (styled by the vendored, scoped training-design.css under `.cdl-scope`):
 * `.page-title`/`.page-sub`, `.card`, `.btn` (+ `.amber`/`.ghost`/`.small`),
 * `.bar` (+ `.green`). So every screen renders 1:1 with the design.
 */
import type { ButtonHTMLAttributes, ReactNode } from 'react';

export function PageHeader({ title, sub }: { title: string; sub?: ReactNode }) {
  return (
    <>
      <h2 className="page-title">{title}</h2>
      {sub && <p className="page-sub">{sub}</p>}
    </>
  );
}

export function Card({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={`card${className ? ` ${className}` : ''}`}>{children}</div>;
}

type Variant = 'primary' | 'ghost' | 'plain';

interface BtnProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
}

// primary = the design's filled accent button (.btn.amber); ghost = outline
// (.btn.ghost); plain = a small ghost (the design has no borderless button, so
// subtle actions use a small ghost).
export function Button({ variant = 'plain', className, ...props }: BtnProps) {
  const cls =
    variant === 'primary'
      ? 'btn amber'
      : variant === 'ghost'
        ? 'btn ghost'
        : 'btn ghost small';
  return <button {...props} className={`${cls}${className ? ` ${className}` : ''}`} />;
}

export function ProgressBar({ pct, className }: { pct: number; className?: string }) {
  const clamped = Math.max(0, Math.min(100, pct));
  return (
    <div className={`bar${clamped >= 100 ? ' green' : ''}${className ? ` ${className}` : ''}`}>
      <div style={{ width: `${clamped}%` }} />
    </div>
  );
}
